import { useMemo, useState } from 'react'
import { Link } from 'react-router'
import { Alert, AlertDescription } from '#app/components/ui/alert.tsx'
import { Badge } from '#app/components/ui/badge.tsx'
import { Button } from '#app/components/ui/button.tsx'
import { Icon } from '#app/components/ui/icon.tsx'
import {
	Card,
	CardContent,
	CardDescription,
	CardHeader,
	CardTitle,
} from '#app/components/ui/card.tsx'
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from '#app/components/ui/select.tsx'
import {
	Table,
	TableBody,
	TableCell,
	TableHead,
	TableHeader,
	TableRow,
} from '#app/components/ui/table.tsx'
import { formatPrice } from '#app/utils/currency.ts'
import {
	DEBRIS_TYPES,
	DUMPSTER_SIZES,
	PROJECT_SIZES,
	PROJECT_TYPES,
	getRecommendation,
	matchListingToSize,
	parseDumpsterAttributes,
	type DebrisTypeValue,
	type DumpsterSizeValue,
	type ProjectSizeValue,
	type ProjectTypeValue,
} from '../dumpster-size-calculator.logic.ts'

type Listing = {
	id: string
	title: string
	slug: string
	dailyRateCents: number | null
	weeklyRateCents: number | null
	monthlyRateCents: number | null
	attributes: unknown
	subcategories: Array<{ slug: string; name: string }>
	cities: Array<{ id: string; name: string; slug: string }>
	ownerBusiness: {
		name: string
		slug: string
		phone: string | null
		website: string | null
	} | null
}

const ALL_CITIES_VALUE = '__all__'

export function DumpsterCalculator({
	listings,
}: {
	listings: Listing[]
}) {
	const [projectType, setProjectType] = useState<ProjectTypeValue | ''>('')
	const [projectSize, setProjectSize] = useState<ProjectSizeValue | ''>('')
	const [debrisType, setDebrisType] = useState<DebrisTypeValue | ''>('')
	const [selectedCityId, setSelectedCityId] = useState<string>('')
	const [sizeOverride, setSizeOverride] = useState<DumpsterSizeValue | null>(
		null,
	)

	// Derive available cities from listings that have dumpster matches
	const cities = useMemo(() => {
		const cityMap = new Map<string, { id: string; name: string; slug: string }>()
		for (const listing of listings) {
			for (const city of listing.cities) {
				if (!cityMap.has(city.id)) {
					cityMap.set(city.id, city)
				}
			}
		}
		return Array.from(cityMap.values()).sort((a, b) =>
			a.name.localeCompare(b.name),
		)
	}, [listings])

	const recommendation = useMemo(() => {
		if (!projectType || !projectSize || !debrisType) return null
		return getRecommendation(projectType, projectSize, debrisType)
	}, [projectType, projectSize, debrisType])

	// Use explicit override if set, otherwise use recommendation
	const activeSize = sizeOverride ?? recommendation?.primarySize ?? null
	const activeSizeInfo = activeSize
		? DUMPSTER_SIZES.find((s) => s.value === activeSize)
		: null

	const filteredListings = useMemo(() => {
		if (!activeSize) return []
		const targetSlug =
			DUMPSTER_SIZES.find((s) => s.value === activeSize)?.subcategorySlug ?? ''

		return listings.filter((listing) => {
			const matchedSlug = matchListingToSize(listing)
			const matchesSize = matchedSlug === targetSlug
			const matchesCity = selectedCityId
				? listing.cities.some((c) => c.id === selectedCityId)
				: true
			return matchesSize && matchesCity
		})
	}, [listings, activeSize, selectedCityId])

	function handleCityChange(value: string) {
		setSelectedCityId(value === ALL_CITIES_VALUE ? '' : value)
	}

	function handleSizeSwitch(size: DumpsterSizeValue) {
		setSizeOverride(size)
	}

	// Reset override when inputs change
	function handleProjectTypeChange(v: ProjectTypeValue) {
		setProjectType(v)
		setSizeOverride(null)
	}
	function handleProjectSizeChange(v: ProjectSizeValue) {
		setProjectSize(v)
		setSizeOverride(null)
	}
	function handleDebrisTypeChange(v: DebrisTypeValue) {
		setDebrisType(v)
		setSizeOverride(null)
	}

	return (
		<section>
			<div className="text-center">
				<Badge variant="secondary" className="mb-4">
					Free Calculator Tool
				</Badge>
				<h1 className="text-3xl font-bold tracking-tight sm:text-4xl">
					DFW Dumpster Size & Cost Calculator
				</h1>
				<p className="text-muted-foreground mx-auto mt-4 max-w-2xl text-lg">
					Answer a few questions to find the right dumpster size for your
					project, then compare prices from Dallas-Fort Worth vendors.
				</p>
			</div>

			<Card className="mt-8">
				<CardHeader>
					<CardTitle>
						<h2 className="flex items-center gap-2 text-xl font-semibold">
							<Icon name="clipboard-list" size="md" className="text-primary" />
							What Size Dumpster Do I Need?
						</h2>
					</CardTitle>
					<CardDescription>
						Select your project details below for an instant size
						recommendation with real DFW vendor pricing.
					</CardDescription>
				</CardHeader>
				<CardContent>
					<div className="grid gap-6 sm:grid-cols-2">
						<div className="space-y-2">
							<label className="flex items-center gap-1.5 text-sm font-medium" htmlFor="project-type">
								<Icon name="layers" size="sm" className="text-muted-foreground" />
								Project Type
							</label>
							<Select
								value={projectType}
								onValueChange={(v) =>
									handleProjectTypeChange(v as ProjectTypeValue)
								}
							>
								<SelectTrigger id="project-type" className="w-full">
									<SelectValue placeholder="Select project type" />
								</SelectTrigger>
								<SelectContent>
									{PROJECT_TYPES.map((pt) => (
										<SelectItem key={pt.value} value={pt.value}>
											{pt.label}
										</SelectItem>
									))}
								</SelectContent>
							</Select>
						</div>

						<div className="space-y-2">
							<label className="flex items-center gap-1.5 text-sm font-medium" htmlFor="project-size">
								<Icon name="package" size="sm" className="text-muted-foreground" />
								Approximate Size
							</label>
							<Select
								value={projectSize}
								onValueChange={(v) =>
									handleProjectSizeChange(v as ProjectSizeValue)
								}
							>
								<SelectTrigger id="project-size" className="w-full">
									<SelectValue placeholder="Select size" />
								</SelectTrigger>
								<SelectContent>
									{PROJECT_SIZES.map((ps) => (
										<SelectItem key={ps.value} value={ps.value}>
											{ps.label}
										</SelectItem>
									))}
								</SelectContent>
							</Select>
						</div>

						<div className="space-y-2">
							<label className="flex items-center gap-1.5 text-sm font-medium" htmlFor="debris-type">
								<Icon name="truck" size="sm" className="text-muted-foreground" />
								Debris Type
							</label>
							<Select
								value={debrisType}
								onValueChange={(v) =>
									handleDebrisTypeChange(v as DebrisTypeValue)
								}
							>
								<SelectTrigger id="debris-type" className="w-full">
									<SelectValue placeholder="Select debris type" />
								</SelectTrigger>
								<SelectContent>
									{DEBRIS_TYPES.map((dt) => (
										<SelectItem key={dt.value} value={dt.value}>
											{dt.label}
										</SelectItem>
									))}
								</SelectContent>
							</Select>
						</div>

						<div className="space-y-2">
							<label className="flex items-center gap-1.5 text-sm font-medium" htmlFor="city">
								<Icon name="map-pin" size="sm" className="text-muted-foreground" />
								City / Area (optional)
							</label>
							<Select
								value={selectedCityId || ALL_CITIES_VALUE}
								onValueChange={handleCityChange}
							>
								<SelectTrigger id="city" className="w-full">
									<SelectValue placeholder="All DFW cities" />
								</SelectTrigger>
								<SelectContent>
									<SelectItem value={ALL_CITIES_VALUE}>
										All DFW cities
									</SelectItem>
									{cities.map((city) => (
										<SelectItem key={city.id} value={city.id}>
											{city.name}
										</SelectItem>
									))}
								</SelectContent>
							</Select>
						</div>
					</div>
				</CardContent>
			</Card>

			{recommendation && activeSizeInfo && (
				<div className="mt-8 space-y-6">
					<Card className="border-green-200 bg-green-50 dark:border-green-900 dark:bg-green-950/50">
						<CardHeader>
							<div className="flex items-center gap-3">
								<div className="flex size-10 items-center justify-center rounded-full bg-green-100 dark:bg-green-900/50">
									<Icon name="check" size="md" className="text-green-600 dark:text-green-400" />
								</div>
								<div className="flex items-center gap-3">
									<Badge variant="default" className="px-3 py-1 text-base">
										{activeSizeInfo.label}
									</Badge>
									<CardTitle>
										{sizeOverride
											? 'Selected Dumpster Size'
											: 'Recommended Dumpster Size'}
									</CardTitle>
								</div>
							</div>
						</CardHeader>
						<CardContent className="space-y-4">
							<p className="text-muted-foreground">
								{sizeOverride
									? `A ${activeSizeInfo.label.toLowerCase()} dumpster (${activeSizeInfo.cubicYards} cubic yards) holds roughly ${activeSizeInfo.truckLoads}, with dimensions of ${activeSizeInfo.dimensions}.`
									: recommendation.explanation}
							</p>

							{!sizeOverride && recommendation.warnings.length > 0 && (
								<div className="space-y-2">
									{recommendation.warnings.map((warning, idx) => (
										<Alert key={idx} variant="warning">
											<AlertDescription>{warning}</AlertDescription>
										</Alert>
									))}
								</div>
							)}

							<div className="flex flex-wrap gap-2">
								{sizeOverride && (
									<Button
										variant="outline"
										size="sm"
										onClick={() => setSizeOverride(null)}
									>
										Back to recommendation ({DUMPSTER_SIZES.find((s) => s.value === recommendation.primarySize)?.label})
									</Button>
								)}
								{recommendation.alternativeSmaller &&
									activeSize !== recommendation.alternativeSmaller && (
										<Button
											variant="outline"
											size="sm"
											onClick={() =>
												handleSizeSwitch(
													recommendation.alternativeSmaller!,
												)
											}
										>
											<Icon name="truck" size="sm" />
											Consider smaller:{' '}
											{
												DUMPSTER_SIZES.find(
													(s) =>
														s.value ===
														recommendation.alternativeSmaller,
												)?.label
											}
										</Button>
									)}
								{recommendation.alternativeLarger &&
									activeSize !== recommendation.alternativeLarger && (
										<Button
											variant="outline"
											size="sm"
											onClick={() =>
												handleSizeSwitch(
													recommendation.alternativeLarger!,
												)
											}
										>
											<Icon name="truck" size="sm" />
											Need more space?{' '}
											{
												DUMPSTER_SIZES.find(
													(s) =>
														s.value ===
														recommendation.alternativeLarger,
												)?.label
											}
										</Button>
									)}
							</div>
						</CardContent>
					</Card>

					{filteredListings.length > 0 && (
						<Card>
							<CardHeader>
								<CardTitle>
									{activeSizeInfo.label} Dumpster Vendors
									{selectedCityId &&
										` in ${cities.find((c) => c.id === selectedCityId)?.name ?? ''}`}
								</CardTitle>
								<CardDescription>
									{filteredListings.length} vendor
									{filteredListings.length !== 1 ? 's' : ''} available
								</CardDescription>
							</CardHeader>
							<CardContent>
								<div className="overflow-x-auto">
									<Table>
										<TableHeader>
											<TableRow>
												<TableHead>Vendor</TableHead>
												<TableHead>Price</TableHead>
												<TableHead>Rental Period</TableHead>
											</TableRow>
										</TableHeader>
										<TableBody>
											{filteredListings.map((listing) => {
												const attrs = parseDumpsterAttributes(
													listing.attributes,
												)
												const price =
													listing.weeklyRateCents ?? listing.dailyRateCents
												return (
													<TableRow key={listing.id}>
														<TableCell className="font-medium">
															{listing.ownerBusiness ? (
																<Link
																	to={`/businesses/${listing.ownerBusiness.slug}`}
																	className="text-primary hover:underline"
																>
																	{listing.ownerBusiness.name}
																</Link>
															) : (
																listing.title
															)}
														</TableCell>
														<TableCell className="font-semibold text-green-600 dark:text-green-400">
															{price ? formatPrice(price) : 'Call for pricing'}
														</TableCell>
														<TableCell>
															{attrs.rentalPeriodDays
																? `${attrs.rentalPeriodDays} days`
																: '7 days'}
														</TableCell>
													</TableRow>
												)
											})}
										</TableBody>
									</Table>
								</div>
							</CardContent>
						</Card>
					)}

					{filteredListings.length === 0 && (
						<Card>
							<CardContent className="flex flex-col items-center py-8 text-center">
								<div className="bg-muted mb-4 flex size-12 items-center justify-center rounded-full">
									<Icon name="truck" size="md" className="text-muted-foreground" />
								</div>
								<p className="text-muted-foreground">
									No vendors currently listed for this size
									{selectedCityId ? ' in this area' : ''}.{' '}
									<Link
										to="/rentals/category/dumpster-and-waste-rental"
										className="text-primary hover:underline"
									>
										Browse all dumpster rentals
									</Link>
								</p>
							</CardContent>
						</Card>
					)}
				</div>
			)}
		</section>
	)
}
